import { useMemo, useState, type CSSProperties } from 'react';
import { nip19 } from 'nostr-tools';
import { BadgeCheck, Check, Copy, Globe, Palette } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/Layout';
import { VideoGrid } from '@/components/VideoGrid';
import { ReportDialog } from '@/components/ReportDialog';
import { FeedFilterDropdown } from '@/components/FeedFilterDropdown';
import { FollowButton } from '@/components/FollowButton';
import { useAuthor } from '@/hooks/useAuthor';
import { useProfileTheme } from '@/hooks/useDittoTheme';
import { useVideoStream } from '@/hooks/useStreamingVideos';
import { useReportsStream } from '@/hooks/useReports';
import { useVideoFeedFilter } from '@/hooks/useVideoFeedFilter';
import { useToast } from '@/hooks/useToast';
import { genUserName } from '@/lib/genUserName';
import { buildThemeBackground, buildThemeStyle, hexToRgba, loadThemeFonts, sanitizeHttpsUrl } from '@/lib/dittoTheme';
import { isVideoReported } from '@/lib/reportUtils';
import { ALL_VIDEO_KINDS, dedupePlebsVideos, isPlebsVideo, isYouTubeUrl } from '@/lib/videoTypes';

function CopyChip({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  return (
    <Button
      variant="secondary"
      size="sm"
      className="gap-1.5 rounded-full text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast({ title: 'Failed to copy', variant: 'destructive' });
        }
      }}
      title={`Copy ${label}`}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {label}
    </Button>
  );
}

export default function ProfilePage({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? genUserName(pubkey);
  const theme = useProfileTheme(pubkey);

  const npub = nip19.npubEncode(pubkey);
  const nprofile = nip19.nprofileEncode({ pubkey });

  const stream = useVideoStream({ authors: [pubkey], kinds: [...ALL_VIDEO_KINDS, 1] });
  const { filter } = useVideoFeedFilter();
  const reports = useReportsStream(true);

  const filtered = useMemo(() => {
    return stream.videos.filter((video) => {
      if (!filter.showYouTube && isYouTubeUrl(video.videoUrl)) return false;
      if (!filter.showUnknownDuration && video.duration === undefined) return false;
      if (!filter.showReported && isVideoReported(video, reports)) return false;
      return true;
    });
  }, [stream.videos, filter, reports]);

  const horizontalVideos = useMemo(
    () => filtered.filter(v => !v.isVertical && v.nipVersion !== 'note-video'),
    [filtered],
  );
  const verticalVideos = useMemo(
    () => filtered.filter(v => v.isVertical && v.nipVersion !== 'note-video'),
    [filtered],
  );
  const noteVideos = useMemo(
    () => filtered.filter(v => v.nipVersion === 'note-video'),
    [filtered],
  );
  const plebsVideos = useMemo(
    () => dedupePlebsVideos(filtered.filter(isPlebsVideo)),
    [filtered],
  );

  // Scoped Ditto theme style for the profile header
  const themeStyle = useMemo(() => {
    if (!theme.data) return undefined;
    loadThemeFonts(theme.data);
    return buildThemeStyle(theme.data);
  }, [theme.data]);

  // Themed section background: theme colors + optional theme bg image with a
  // translucent overlay of the theme's background color.
  const sectionStyle = useMemo(() => {
    if (!theme.data || !themeStyle) return undefined;
    return { ...themeStyle, ...buildThemeBackground(theme.data) } as CSSProperties;
  }, [theme.data, themeStyle]);

  // Banner strip: the kind 0 banner image ALWAYS wins — a theme bg image
  // must never replace it. Without a banner, a translucent theme-color tint
  // lets the section's bg image continue through the strip.
  const bannerUrl = sanitizeHttpsUrl(metadata?.banner);
  const bannerStyle = useMemo(() => {
    const style: CSSProperties = {};
    if (bannerUrl) {
      const bg = theme.data?.colors.background ?? '#000000';
      style.backgroundImage =
        `linear-gradient(${hexToRgba(bg, 0.15)}, ${hexToRgba(bg, 0.4)}), url("${bannerUrl}")`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
    } else if (theme.data?.bg) {
      const c = theme.data.colors.background;
      style.backgroundImage = `linear-gradient(${hexToRgba(c, 0.55)}, ${hexToRgba(c, 0.8)})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
    }
    return style;
  }, [bannerUrl, theme.data]);

  const themed = !!sectionStyle;

  return (
    <Layout>
      <div style={themed ? sectionStyle : undefined}>
        {/* Banner */}
        <div className="h-40 w-full bg-gradient-to-r from-accent via-muted to-accent sm:h-52" style={bannerStyle} />

        <div className="mx-auto max-w-5xl px-3 sm:px-6">
          {/* Profile header */}
          <div className="relative -mt-12 mb-6 flex flex-col gap-4 sm:-mt-16 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <Avatar className="h-24 w-24 border-4 border-background sm:h-32 sm:w-32">
                <AvatarImage src={metadata?.picture} alt={name} />
                <AvatarFallback className="text-2xl">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="pb-1 min-w-0">
                <h1 className="flex items-center gap-1.5 text-2xl font-bold truncate">
                  {name}
                  {metadata?.nip05 && (
                    <span title={metadata.nip05}>
                      <BadgeCheck className="h-5 w-5 text-primary" />
                    </span>
                  )}
                </h1>
                {metadata?.nip05 && (
                  <p className="truncate text-sm text-muted-foreground">{metadata.nip05}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FollowButton pubkey={pubkey} />
              {themed && theme.data?.title && (
                <Badge variant="secondary" className="gap-1">
                  <Palette className="h-3 w-3" /> {theme.data.title}
                </Badge>
              )}
              <CopyChip label="npub" value={npub} />
              <CopyChip label="nprofile" value={nprofile} />
              <ReportDialog profilePubkey={pubkey} />
            </div>
          </div>

          {metadata?.about && (
            <p className="mb-4 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
              {metadata.about}
            </p>
          )}
          {metadata?.website && (
            <a
              href={metadata.website}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <Globe className="h-3.5 w-3.5" /> {metadata.website}
            </a>
          )}
        </div>
      </div>

      {/* Videos / Shorts */}
      <div className="mx-auto max-w-5xl px-3 sm:px-6 py-6">
        <Tabs defaultValue="videos">
          <div className="mb-4 flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="videos">Videos</TabsTrigger>
              <TabsTrigger value="shorts">Shorts</TabsTrigger>
              {filter.showNoteVideos && <TabsTrigger value="notes">Notes</TabsTrigger>}
              <TabsTrigger value="plebs" title="Videos this user uploaded via plebs.app (#pv69420)">
                Plebs
              </TabsTrigger>
            </TabsList>
            <FeedFilterDropdown />
          </div>
          <TabsContent value="videos">
            <VideoGrid
              videos={horizontalVideos}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              emptyMessage="This user hasn't published any videos yet."
            />
          </TabsContent>
          <TabsContent value="shorts">
            <VideoGrid
              videos={verticalVideos}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              variant="shorts"
              emptyMessage="This user hasn't published any shorts yet."
            />
          </TabsContent>
          {filter.showNoteVideos && (
            <TabsContent value="notes">
              <VideoGrid
                videos={noteVideos}
                isLoading={stream.isLoading}
                isLoadingMore={stream.isLoadingMore}
                hasMore={stream.hasMore}
                onLoadMore={stream.loadMore}
                isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
                emptyMessage="This user hasn't posted any notes with video files yet."
              />
            </TabsContent>
          )}
          <TabsContent value="plebs">
            <VideoGrid
              videos={plebsVideos}
              isLoading={stream.isLoading}
              isLoadingMore={stream.isLoadingMore}
              hasMore={stream.hasMore}
              onLoadMore={stream.loadMore}
              isReported={filter.showReported ? (v) => isVideoReported(v, reports) : undefined}
              emptyMessage="This user has no plebs.app uploads (#pv69420). plebs.app published each video as three events; surviving ones are shown even when only one remains."
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export function ProfilePageSkeleton() {
  return (
    <Layout>
      <Skeleton className="h-40 w-full sm:h-52" />
      <div className="mx-auto max-w-5xl px-3 sm:px-6">
        <div className="-mt-12 mb-6 flex items-end gap-4 sm:-mt-16">
          <Skeleton className="h-24 w-24 rounded-full border-4 border-background sm:h-32 sm:w-32" />
          <div className="space-y-2 pb-1">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
    </Layout>
  );
}

export function ProfileNotFound() {
  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-3 sm:px-6 py-16">
        <Card className="border-dashed">
          <CardContent className="py-12 px-8 text-center">
            <p className="font-medium">Profile not found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This profile could not be loaded from your relays.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
